import React, { useMemo } from 'react';
import Layout from './Layout';
import OverviewScreen from '../screens/OverviewScreen';
import CrewMovementScreen from '../screens/CrewMovementScreen';
import CounterDistributionScreen from '../screens/CounterDistributionScreen';
import ReportsScreen from '../screens/ReportsScreen';
import AdminScreen from '../screens/AdminScreen';
import SecurityScreen from '../screens/SecurityScreen';
import ArrivalDashboardScreen from '../screens/ArrivalDashboardScreen';
import { Tab, UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { ChartPieIcon, UsersIcon, TableCellsIcon, DocumentChartBarIcon, CogIcon, BarcodeIcon, ArrowDownTrayIcon } from './Icons';
import AppTour from './AppTour';

interface MainLayoutProps {
  activeTab: Tab;
  setActiveTab: (tab: Tab) => void;
  showTour: boolean;
  onTourComplete: () => void;
}

interface TabConfig {
  id: Tab;
  label: string;
  icon: React.ReactNode;
  roles: UserRole[];
  group: 'monitoring' | 'operations' | 'admin';
  color: 'blue' | 'green' | 'amber' | 'red' | 'orange' | 'fuchsia';
}

const TABS_CONFIG: TabConfig[] = [
  { id: Tab.FlightDashboard, label: 'Dashboard', icon: <ChartPieIcon className="w-5 h-5 mr-2"/>, roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT, UserRole.VIEWER, UserRole.LL], group: 'monitoring', color: 'blue' },
  { id: Tab.CrewMovement, label: 'Crew Movement', icon: <UsersIcon className="w-5 h-5 mr-2"/>, roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT], group: 'operations', color: 'green' },
  { id: Tab.ArrivalDashboard, label: 'Arrival', icon: <ArrowDownTrayIcon className="w-5 h-5 mr-2"/>, roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT, UserRole.LL], group: 'operations', color: 'orange' },
  { id: Tab.CounterDistribution, label: 'Counters', icon: <TableCellsIcon className="w-5 h-5 mr-2"/>, roles: [UserRole.ADMIN, UserRole.SUPERVISOR], group: 'operations', color: 'amber' },
  { id: Tab.Security, label: 'Security', icon: <BarcodeIcon className="w-5 h-5 mr-2"/>, roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT], group: 'operations', color: 'red' },
  { id: Tab.Reports, label: 'Reports', icon: <DocumentChartBarIcon className="w-5 h-5 mr-2"/>, roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT, UserRole.VIEWER, UserRole.LL], group: 'operations', color: 'fuchsia' },
  { id: Tab.Admin, label: 'Admin Panel', icon: <CogIcon className="w-5 h-5 mr-2"/>, roles: [UserRole.ADMIN], group: 'admin', color: 'fuchsia' },
];

const MainLayout: React.FC<MainLayoutProps> = ({ activeTab, setActiveTab, showTour, onTourComplete }) => {
  const { user } = useAuth();

  const groupedTabs = useMemo(() => {
    let availableTabs: TabConfig[];
    if (user?.role === UserRole.LL) {
        availableTabs = TABS_CONFIG.filter(tab => 
            tab.id === Tab.FlightDashboard || 
            tab.id === Tab.ArrivalDashboard ||
            tab.id === Tab.Reports
        );
    } else {
        availableTabs = TABS_CONFIG.filter(tab => user && tab.roles.includes(user.role));
    }

    return availableTabs.reduce((acc, tab) => {
      const group = tab.group;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(tab);
      return acc;
    }, {} as { [key: string]: TabConfig[] });
  }, [user]);

  const colorClasses = {
      blue: {
          active: 'bg-gradient-to-t from-blue-600 to-blue-500 border-blue-300 text-white shadow-[0_0_15px_rgba(59,130,246,0.6),inset_0_1px_0_rgba(255,255,255,0.15)]',
          text: 'text-blue-400',
          hover: 'hover:bg-blue-900/50 hover:text-blue-300',
          focus: 'focus:ring-blue-500'
      },
      green: {
          active: 'bg-gradient-to-t from-green-600 to-green-500 border-green-300 text-white shadow-[0_0_15px_rgba(34,197,94,0.6),inset_0_1px_0_rgba(255,255,255,0.15)]',
          text: 'text-green-400',
          hover: 'hover:bg-green-900/50 hover:text-green-300',
          focus: 'focus:ring-green-500'
      },
      amber: {
          active: 'bg-gradient-to-t from-amber-600 to-amber-500 border-amber-300 text-white shadow-[0_0_15px_rgba(245,158,11,0.6),inset_0_1px_0_rgba(255,255,255,0.15)]',
          text: 'text-amber-400',
          hover: 'hover:bg-amber-900/50 hover:text-amber-300',
          focus: 'focus:ring-amber-500'
      },
      red: {
          active: 'bg-gradient-to-t from-red-600 to-red-500 border-red-300 text-white shadow-[0_0_15px_rgba(239,68,68,0.6),inset_0_1px_0_rgba(255,255,255,0.15)]',
          text: 'text-red-400',
          hover: 'hover:bg-red-900/50 hover:text-red-300',
          focus: 'focus:ring-red-500'
      },
      orange: {
          active: 'bg-gradient-to-t from-orange-600 to-orange-500 border-orange-300 text-white shadow-[0_0_15px_rgba(249,115,22,0.6),inset_0_1px_0_rgba(255,255,255,0.15)]',
          text: 'text-orange-400',
          hover: 'hover:bg-orange-900/50 hover:text-orange-300',
          focus: 'focus:ring-orange-500'
      },
      fuchsia: {
          active: 'bg-gradient-to-t from-fuchsia-600 to-fuchsia-500 border-fuchsia-300 text-white shadow-[0_0_15px_rgba(217,70,239,0.6),inset_0_1px_0_rgba(255,255,255,0.15)]',
          text: 'text-fuchsia-400',
          hover: 'hover:bg-fuchsia-900/50 hover:text-fuchsia-300',
          focus: 'focus:ring-fuchsia-500'
      }
  };

  const renderContent = () => {
    switch (activeTab) {
      case Tab.FlightDashboard: return <OverviewScreen />;
      case Tab.CrewMovement: return <CrewMovementScreen />;
      case Tab.ArrivalDashboard: return <ArrivalDashboardScreen />;
      case Tab.CounterDistribution: return <CounterDistributionScreen />;
      case Tab.Security: return <SecurityScreen />;
      case Tab.Reports: return <ReportsScreen />;
      case Tab.Admin: return <AdminScreen />;
      default: return <OverviewScreen />;
    }
  };

  const renderTabButton = (tab: TabConfig) => {
    const tabColorStyle = colorClasses[tab.color];
    const isActive = activeTab === tab.id;
    const isDashboard = tab.id === Tab.FlightDashboard;

    // Base classes for all tabs
    const baseClasses = 'flex items-center whitespace-nowrap py-3 px-3 sm:px-4 border-b-2 font-medium text-sm transition-all duration-300 rounded-t-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900';

    // Classes for active vs. inactive states
    const activeStateClasses = isActive
      ? `${tabColorStyle.active} font-semibold`
      : `border-transparent ${tabColorStyle.text} ${tabColorStyle.hover}`;
    
    // Special classes to make the dashboard tab distinct
    const dashboardDistinctClasses = isDashboard
      ? isActive
        // When dashboard is active: make it pop with scale, flicker-glow, and z-index
        ? 'transform scale-105 z-10 animate-flicker-glow'
        // When dashboard is inactive: give it a slightly different background and make it flicker
        : 'bg-slate-800/50 animate-flicker-glow'
      : '';

    const finalClasses = [
      baseClasses,
      tabColorStyle.focus,
      activeStateClasses,
      dashboardDistinctClasses,
    ].filter(Boolean).join(' ');

    return (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        data-tour-id={tab.id === Tab.Reports ? 'reports-tab' : undefined}
        className={finalClasses}
      >
        {tab.icon}
        {tab.label}
      </button>
    );
  };

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="no-print mb-4 border-b border-slate-700 flex justify-between items-center">
          <nav data-tour-id="main-tabs" className="flex flex-wrap -mb-px items-center flex-grow" aria-label="Tabs">
            
            {groupedTabs.monitoring?.map(renderTabButton)}
            
            {groupedTabs.operations?.map(renderTabButton)}

            <div className="flex-grow"></div>
            
            {groupedTabs.admin?.map(renderTabButton)}

          </nav>
        </div>
        <div className="flex-grow animate-slide-up-fade-in">
          {renderContent()}
        </div>
      </div>
      <AppTour isOpen={showTour} onComplete={onTourComplete} setActiveTab={setActiveTab} />
    </Layout>
  );
};

export default MainLayout;
